import { iaRa } from 'lib/helpers/utility';
import { formatDate } from 'lib/helpers/dateUtils';

import { showAlert } from 'components/shared/Alert/Alert';

import { BILLING_ADDRESS_FIELDS } from './constants';
import { getPlanAmountCapError } from './planDuration';

export const EMPTY_BILLING_ADDRESS = Object.fromEntries(
  BILLING_ADDRESS_FIELDS.map((field) => [field, ''])
);

export const getPatientBillingAddress = (address) =>
  Object.fromEntries(BILLING_ADDRESS_FIELDS.map((field) => [field, address?.[field] || '']));

export const getPatientFullName = (appointment) =>
  [appointment?.patient?.f_name, appointment?.patient?.l_name].filter(Boolean).join(' ');

export const buildPreAuthInitialValues = ({
  appointment,
  patientName,
  practiceId,
  patientId,
  defaultScope,
  processor,
  isPayroc,
  timezone,
  existingSignature,
  initialCardId
}) => ({
  cardholderName: !isPayroc ? patientName || getPatientFullName(appointment) : '',
  signature: existingSignature || '',
  date: formatDate(new Date(), timezone),
  type: appointment?.type || 'annual',
  practiceId: practiceId || appointment?.practice?.id || '',
  patientId: patientId || appointment?.patient?.id || '',
  appointmentId: appointment?.id || null,
  cardId: initialCardId || null,
  ...EMPTY_BILLING_ADDRESS,
  // A prefilled signature is an authorization that was already given — starting at `false`
  // made every reinitialize of the form drop it until the patient signed a second time.
  authorized: !!existingSignature,
  understood: false,
  scope: defaultScope,
  consent: null,
  processor
});

export const getPreAuthSaveAmount = ({ isRecurring, preAuthAmount, consent }) => {
  if (isRecurring) return null;
  if (consent?.capAmount != null) return consent.capAmount;
  return Number.isFinite(Number(preAuthAmount)) ? Number(preAuthAmount) : null;
};

export const buildBillingAddress = (values) => ({
  address_ln_1: values?.address_ln_1 || '',
  address_ln_2: values?.address_ln_2 || '',
  city: values?.city || '',
  state: values?.state || '',
  zip: values?.zip || ''
});

// A card that is already tokenized only needs the authorization record, not a new charge.
// `isAfterCharge` means the money is already taken, so a failure here must say so explicitly.
export const authorizeStoredCard = async ({
  createPreAuthAsync,
  formValues,
  cards,
  patientName,
  cardId,
  signatureDate,
  amount,
  isAfterCharge = false
}) => {
  const card = iaRa(cards).find((item) => item.id === cardId);

  const failureTitle = isAfterCharge
    ? "Payment went through, but the card couldn't be authorized"
    : "Couldn't authorize the selected card";

  let response;
  try {
    response = await createPreAuthAsync({
      patientId: formValues.patientId,
      practiceId: formValues.practiceId,
      cardId,
      type: 'card',
      patientName,
      cardholderName: formValues.cardholderName || card?.name || patientName,
      cardholderSignature: formValues.signature,
      date: signatureDate,
      amount,
      scope: formValues.scope,
      appointmentId: formValues.appointmentId,
      consent: formValues.consent
    });
  } catch (error) {
    showAlert({
      title: failureTitle,
      message: error?.message || 'Please try again or contact support.',
      color: 'danger'
    });
    return false;
  }

  if (response?.code !== 0) {
    showAlert({
      title: failureTitle,
      message: response?.error || 'Please try again or contact support.',
      color: 'danger'
    });
    return false;
  }

  return true;
};

// A plan that breaches the program's per-payment cap must not reach the processor.
export const isPlanWithinAmountCap = ({ values, recurringCaps }) => {
  if (values?.method !== 'recurring-charge') return true;

  const capError = getPlanAmountCapError({
    amount: values?.amount,
    recurringPayment: values?.recurringPayment,
    minAmountPerPayment: recurringCaps?.minAmountPerPayment
  });
  if (!capError) return true;

  showAlert({
    title: 'This payment plan exceeds the per-payment limit',
    message: capError,
    color: 'danger'
  });
  return false;
};
